import { emptyProgress, parseProgress, type TourProgress } from './tour-machine'

/**
 * This browser's copy of a person's tour progress, one record per login. The
 * server holds the real one, so it follows them to other devices; this copy
 * covers the moments it can't be reached. Private windows and blocked storage
 * throw; the tours still work, they just aren't remembered here.
 */

const PREFIX = 'helm:onboarding:'
const SYNC_PREFIX = 'helm:onboarding-sync:'

export function readProgress(userId: string): TourProgress {
  try {
    return parseProgress(window.localStorage.getItem(PREFIX + userId))
  } catch {
    return emptyProgress()
  }
}

export function writeProgress(userId: string, progress: TourProgress) {
  try {
    window.localStorage.setItem(PREFIX + userId, JSON.stringify(progress))
  } catch {
    // Storage full or blocked: nothing to do.
  }
}

export interface SyncStatus {
  /** This browser's copy has gone to the server once: it is not imported again. */
  imported: boolean
  /** A change here has not reached the server yet. */
  unsaved: boolean
}

export function readSyncStatus(userId: string): SyncStatus {
  try {
    const value = JSON.parse(window.localStorage.getItem(SYNC_PREFIX + userId) ?? '{}') as unknown
    const status = (value && typeof value === 'object' ? value : {}) as Partial<SyncStatus>
    return { imported: status.imported === true, unsaved: status.unsaved === true }
  } catch {
    return { imported: false, unsaved: false }
  }
}

export function writeSyncStatus(userId: string, status: SyncStatus) {
  try {
    window.localStorage.setItem(SYNC_PREFIX + userId, JSON.stringify(status))
  } catch {
    // Not remembered: the next visit sends this browser's copy again, which is harmless.
  }
}
